import { useState } from 'react'
import { AlertTriangle, Bug, Send, CheckCircle } from 'lucide-react'
import { captureError, captureMessage, Sentry } from '@/lib/sentry'

export default function TestError() {
  const [messageSent, setMessageSent] = useState(false)
  const [exceptionSent, setExceptionSent] = useState(false)
  const [shouldThrowError, setShouldThrowError] = useState(false)

  if (shouldThrowError) {
    throw new Error('Erro de teste nao tratado - Sentry')
  }

  const triggerCapturedError = () => {
    try {
      throw new Error('Erro capturado manualmente - Sentry')
    } catch (error) {
      captureError(error as Error, { test: true, page: 'TestError' })
      setExceptionSent(true)
      setTimeout(() => setExceptionSent(false), 3000)
    }
  }

  const sendTestMessage = () => {
    captureMessage('Mensagem de teste do Sentry', 'info')
    setMessageSent(true)
    setTimeout(() => setMessageSent(false), 3000)
  }

  const triggerBreadcrumbTest = () => {
    Sentry.addBreadcrumb({
      category: 'test',
      message: 'Usuario clicou no botao de breadcrumb',
      level: 'info',
    })

    Sentry.addBreadcrumb({
      category: 'test',
      message: 'Acao secundaria executada',
      level: 'info',
    })

    captureMessage('Teste de breadcrumbs - Sentry', 'info')
    setMessageSent(true)
    setTimeout(() => setMessageSent(false), 3000)
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Teste do Sentry
        </h1>
        <p className="text-gray-600">
          Use os botoes abaixo para testar a integracao com o Sentry. Verifique o painel do Sentry para confirmar que os eventos estao sendo recebidos.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="font-medium text-gray-900">Erro Nao Tratado</h2>
              <p className="text-sm text-gray-500">Dispara um erro que sera capturado pelo ErrorBoundary</p>
            </div>
          </div>
          <button
            onClick={() => setShouldThrowError(true)}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Disparar Erro
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Bug className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="font-medium text-gray-900">Excecao Capturada</h2>
              <p className="text-sm text-gray-500">Captura uma excecao manualmente e envia ao Sentry</p>
            </div>
          </div>
          <button
            onClick={triggerCapturedError}
            className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
          >
            {exceptionSent ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Enviado!
              </>
            ) : (
              'Capturar Excecao'
            )}
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Send className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-medium text-gray-900">Mensagem de Teste</h2>
              <p className="text-sm text-gray-500">Envia uma mensagem informativa ao Sentry</p>
            </div>
          </div>
          <button
            onClick={sendTestMessage}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            {messageSent ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Enviado!
              </>
            ) : (
              'Enviar Mensagem'
            )}
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Bug className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-medium text-gray-900">Teste de Breadcrumbs</h2>
              <p className="text-sm text-gray-500">Adiciona breadcrumbs e envia ao Sentry</p>
            </div>
          </div>
          <button
            onClick={triggerBreadcrumbTest}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            {messageSent ? (
              <>
                <CheckCircle className="w-4 h-4" />
                Enviado!
              </>
            ) : (
              'Testar Breadcrumbs'
            )}
          </button>
        </div>
      </div>

      <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-2">Informacoes</h3>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• DSN configurado: {import.meta.env.VITE_SENTRY_DSN ? 'Sim' : 'Nao'}</li>
          <li>• Ambiente: {import.meta.env.MODE}</li>
          <li>• Os erros capturados aparecerao no painel do Sentry em alguns segundos</li>
        </ul>
      </div>
    </div>
  )
}
